import jwtDecode from 'jwt-decode';
import { SsoOpenIdConfig } from './contracts';
import { generatePkceData } from './pkce';

export default class OidcClient {
  constructor(
    private readonly openIdConfig: SsoOpenIdConfig,
    private readonly clientId: string,
    private readonly state?: State
  ) {}

  getAuthority() {
    return this.openIdConfig.issuer;
  }

  getClientId() {
    return this.clientId;
  }

  async authorizeRedirect(scope: string, redirectUri: string) {
    const [codeVerifier, codeChallenge] = await generatePkceData();

    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();

    const stateObj: State = {
      codeVerifier,
      nonce,
      openIdConfig: this.openIdConfig,
      clientId: this.clientId,
      redirectUri,
    };

    sessionStorage.setItem(`oidc.${state}`, JSON.stringify(stateObj));

    const params = new URLSearchParams();
    params.set('client_id', this.clientId);
    params.set('redirect_uri', redirectUri);
    params.set('scope', scope);
    params.set('response_type', 'code');
    params.set('code_challenge', codeChallenge);
    params.set('code_challenge_method', 'S256');
    params.set('state', state);
    params.set('nonce', nonce);

    window.location.replace(`${this.openIdConfig.authorization_endpoint}?${params.toString()}`);
  }

  async getToken(code: string): Promise<TokenResponse> {
    if (!this.state) return {};

    try {
      const params = new URLSearchParams();

      params.append('grant_type', 'authorization_code');
      params.append('code', code);
      params.append('code_verifier', this.state.codeVerifier);
      params.append('client_id', this.clientId);
      params.append('redirect_uri', this.state.redirectUri);

      const response = await fetch(this.openIdConfig.token_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.status === 400) {
        return await response.json();
      }

      if (response.status === 401) {
        return {};
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch access token: ${response.status} ${response.statusText}`);
      }

      const tokenResponse: TokenResponse = await response.json();
      if (!tokenResponse.id_token) return {};

      // Verify nonce
      const tokenData: any = jwtDecode(tokenResponse.id_token);
      if (tokenData.nonce === this.state.nonce) return tokenResponse;

      return {};
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  static fromState(state: string): OidcClient | undefined {
    const storageKey = `oidc.${state}`;
    const stateJson = sessionStorage.getItem(storageKey);
    if (!stateJson) return;

    const stateObj: State = JSON.parse(stateJson);

    // Remove storage item to avoid reusing authentication flow
    sessionStorage.removeItem(storageKey);

    // No need redirect uri at this state
    return new OidcClient(stateObj.openIdConfig, stateObj.clientId, stateObj);
  }
}

type State = {
  codeVerifier: string;
  nonce: string;
  openIdConfig: SsoOpenIdConfig;
  clientId: string;
  redirectUri: string;
};

type TokenResponse = {
  error?: string;
  error_description?: string;
  id_token?: string;
  access_token?: string;
};
